import { useState } from 'react';
import AnimatedLogo from './AnimatedLogo';
import ExtensionsPage from './ExtensionsPage';
import DidsPage from './DidsPage';
import TrunksPage from './TrunksPage';
import AsteriskPage from './AsteriskPage';
import AsteriskSyncPanel from './AsteriskSyncPanel';
import RegisteredDevicesPage from './RegisteredDevicesPage';
import TrunkStatusPage from './TrunkStatusPage';
import OutboundRoutesPage from './OutboundRoutesPage';
import DiagnosticsPage from './DiagnosticsPage';
import ConferenceRoomsPage from './ConferenceRoomsPage';

type AstSection =
  | 'Overview'
  | 'Extensions'
  | 'DIDs'
  | 'Trunks'
  | 'IVR & Call Flow'
  | 'Devices'
  | 'Outbound Routes'
  | 'Trunk Status'
  | 'Diagnostics'
  | 'Sync Queue'
  | 'Conference Rooms';

const NAV_ITEMS: { label: AstSection; icon: string; group?: string }[] = [
  { label: 'Overview',       icon: '▦' },
  { label: 'Extensions',     icon: '☎',  group: 'Config' },
  { label: 'DIDs',           icon: '🔢', group: 'Config' },
  { label: 'Trunks',         icon: '🔗', group: 'Config' },
  { label: 'Outbound Routes', icon: '🛣', group: 'Config' },
  { label: 'IVR & Call Flow',icon: '🔀', group: 'Config' },
  { label: 'Devices',        icon: '📱', group: 'Monitor' },
  { label: 'Trunk Status',   icon: '📡', group: 'Monitor' },
  { label: 'Diagnostics',    icon: '🔬', group: 'Monitor' },
  { label: 'Sync Queue',     icon: '⟳',  group: 'System' },
  { label: 'Conference Rooms', icon: '🎙', group: 'Config' },
];

interface Props {
  user: any;
  onLogout: () => void;
}

export default function AsteriskAdminPanel({ user, onLogout }: Props) {
  const [active, setActive] = useState<AstSection>('Overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#0f172a', overflow: 'hidden' }}>
      {/* Sidebar */}
      <div style={{
        width: sidebarOpen ? 220 : 60,
        minWidth: sidebarOpen ? 220 : 60,
        background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
        borderRight: '1px solid rgba(139,92,246,0.15)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s, min-width 0.2s',
        overflow: 'hidden',
      }}>
        {/* Logo */}
        <div style={{ padding: '18px 12px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <AnimatedLogo width={sidebarOpen ? 140 : 36} style={{ display: 'block', margin: '0 auto' }} />
          {sidebarOpen && (
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#f9fafb' }}>Asterisk Admin</div>
              <div style={{ fontSize: 11, color: '#a78bfa', fontWeight: 500, marginTop: 2 }}>PBX Configuration</div>
            </div>
          )}
        </div>

        {/* User pill */}
        {sidebarOpen && (
          <div style={{
            margin: '10px 10px 4px',
            padding: '8px 10px',
            background: 'rgba(139,92,246,0.12)',
            borderRadius: 8,
            fontSize: 12,
            color: '#c4b5fd',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}>
            <span style={{ fontSize: 14 }}>🛡</span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.display_name || user?.username}
            </span>
          </div>
        )}

        {/* Nav items */}
        <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
          {NAV_ITEMS.map(({ label, icon, group }, idx) => {
            const isActive = active === label;
            const showGroupLabel = sidebarOpen && group && (idx === 0 || NAV_ITEMS[idx - 1].group !== group);
            return (
              <div key={label}>
                {showGroupLabel && (
                  <div style={{ fontSize: 9, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '1px', padding: '10px 16px 4px' }}>
                    {group}
                  </div>
                )}
                <div
                  onClick={() => setActive(label)}
                  title={!sidebarOpen ? label : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: sidebarOpen ? '9px 14px' : '9px',
                    margin: '1px 6px', borderRadius: 8, cursor: 'pointer',
                    background: isActive
                      ? 'linear-gradient(90deg, rgba(139,92,246,0.25) 0%, rgba(139,92,246,0.08) 100%)'
                      : 'transparent',
                    borderLeft: isActive ? '3px solid #8b5cf6' : '3px solid transparent',
                    color: isActive ? '#e9d5ff' : '#94a3b8',
                    fontWeight: isActive ? 700 : 400, fontSize: 13,
                    transition: 'all 0.15s',
                    justifyContent: sidebarOpen ? 'flex-start' : 'center',
                  }}
                  onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(139,92,246,0.1)'; }}
                  onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <span style={{ fontSize: 15, lineHeight: 1 }}>{icon}</span>
                  {sidebarOpen && <span>{label}</span>}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Collapse toggle + logout */}
        <div style={{ padding: '10px 6px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <div
            onClick={() => setSidebarOpen(o => !o)}
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 10px', borderRadius: 8,
              cursor: 'pointer', color: '#64748b', fontSize: 12,
              justifyContent: sidebarOpen ? 'flex-start' : 'center',
            }}
          >
            <span style={{ fontSize: 16 }}>{sidebarOpen ? '◀' : '▶'}</span>
            {sidebarOpen && <span>Collapse</span>}
          </div>
          <div
            onClick={onLogout}
            title={!sidebarOpen ? 'Logout' : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 10px', borderRadius: 8,
              cursor: 'pointer', color: '#f87171', fontSize: 12,
              justifyContent: sidebarOpen ? 'flex-start' : 'center',
            }}
          >
            <span style={{ fontSize: 16 }}>⏻</span>
            {sidebarOpen && <span>Logout</span>}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top bar */}
        <div style={{
          height: 52,
          background: 'rgba(15,23,42,0.95)',
          borderBottom: '1px solid rgba(139,92,246,0.12)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 20px',
          gap: 12,
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 18 }}>
            {NAV_ITEMS.find(n => n.label === active)?.icon ?? '▦'}
          </span>
          <span style={{ fontWeight: 700, fontSize: 16, color: '#f1f5f9' }}>{active}</span>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: '#475569' }}>
            Telephony Engine Portal
          </span>
        </div>

        {/* Page content */}
        <div style={{ flex: 1, overflow: 'auto', background: '#0f172a' }}>
          {active === 'Overview'        && <AsteriskOverview onNavigate={setActive} />}
          {active === 'Extensions'      && <ExtensionsPage showTenantSelector />}
          {active === 'DIDs'            && <DidsPage showTenantSelector />}
          {active === 'Trunks'          && <TrunksPage user={user} />}
          {active === 'Outbound Routes'  && <OutboundRoutesPage showTenantSelector />}
          {active === 'IVR & Call Flow' && <AsteriskPage />}
          {active === 'Devices'         && <RegisteredDevicesPage />}
          {active === 'Trunk Status'    && <TrunkStatusPage />}
          {active === 'Diagnostics'     && <DiagnosticsPage />}
          {active === 'Sync Queue'      && <AsteriskSyncPanel />}
          {active === 'Conference Rooms' && <ConferenceRoomsPage showTenantSelector />}
        </div>
      </div>
    </div>
  );
}

// ── Overview card ─────────────────────────────────────────────────────────────

function AsteriskOverview({ onNavigate }: { onNavigate: (s: AstSection) => void }) {
  const cards: { label: AstSection; icon: string; desc: string; color: string }[] = [
    { label: 'Extensions',      icon: '☎',  desc: 'SIP extensions and softphones',          color: '#3b82f6' },
    { label: 'DIDs',            icon: '🔢', desc: 'Inbound DID numbers and routing',         color: '#10b981' },
    { label: 'Trunks',          icon: '🔗', desc: 'SIP trunks and PSTN gateways',            color: '#f59e0b' },
    { label: 'Outbound Routes', icon: '🛣',  desc: 'Per-destination routing to SIP trunks',   color: '#14b8a6' },
    { label: 'IVR & Call Flow', icon: '🔀', desc: 'IVR menus, ring groups, time rules',      color: '#8b5cf6' },
    { label: 'Devices',         icon: '📱', desc: 'Registered SIP devices and RTT status',   color: '#0ea5e9' },
    { label: 'Trunk Status',    icon: '📡', desc: 'Trunk reachability with OPTIONS ping',    color: '#f97316' },
    { label: 'Diagnostics',     icon: '🔬', desc: 'Ping tools, code lookup, call analysis',  color: '#ec4899' },
    { label: 'Sync Queue',      icon: '⟳',  desc: 'Pending config sync',    color: '#06b6d4' },
    { label: 'Conference Rooms', icon: '🎙', desc: '8XXX audio conference bridges with PIN support', color: '#a855f7' },
  ];

  return (
    <div style={{ padding: '32px 28px' }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', marginBottom: 6 }}>
          Telephony Engine
        </div>
        <div style={{ fontSize: 14, color: '#64748b' }}>
          Manage PBX objects. Changes are saved to the local database and synced to FS3 on schedule.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
        {cards.map(c => (
          <div
            key={c.label}
            onClick={() => onNavigate(c.label)}
            style={{
              background: 'rgba(30,41,59,0.7)',
              border: `1px solid ${c.color}30`,
              borderRadius: 14,
              padding: '20px 18px',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.background = `${c.color}18`;
              (e.currentTarget as HTMLElement).style.borderColor = `${c.color}60`;
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(30,41,59,0.7)';
              (e.currentTarget as HTMLElement).style.borderColor = `${c.color}30`;
            }}
          >
            <div style={{ fontSize: 28, marginBottom: 10 }}>{c.icon}</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#e2e8f0', marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{c.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
